/**
 * Si el sitio está en línea, y el mando para cambiarlo.
 *
 * ── Por qué vive en dos pantallas ───────────────────────────────────────────
 * En el índice de Configuración porque es el estado del que cuelga todo lo
 * demás —sin sitio publicado, las direcciones no responden y las páginas de
 * reserva no existen— y ahí se ve sin entrar a nada. Y dentro de Publicación
 * porque es la pantalla que trata de esto.
 *
 * En un componente y no copiado: son dos sitios que tienen que decir lo mismo,
 * y dos copias de un estado acaban discrepando el día que una cambia.
 *
 * ── Por qué sólo se puede retirar ───────────────────────────────────────────
 * Publicar no es una llamada: el editor construye el HTML de cada página en el
 * navegador y lo sube por tandas antes de avisar al servidor. El panel no tiene
 * ese generador, así que un botón de «Publicar» aquí sólo podría mentir. Dice a
 * dónde lleva —al editor— en vez de fingir que publica.
 */
import { useState } from 'react'
import { IconCloudOff, IconLayout, IconLoader2, IconRocket, IconWorldOff } from '@tabler/icons-react'
import { ConfirmDialog } from '../ConfirmDialog'
import { useI18n } from '../../../lib/i18n'
import { unpublishSite } from '../../../lib/api'

export function SitePublishState({ projectId, token, publishedSubdomain, onOpenEditor, onUnpublished }: {
  projectId: string
  token: string
  publishedSubdomain: string | null
  /** Al editor, que es donde se construyen las páginas al publicar. */
  onOpenEditor?: (() => void) | undefined
  /** Para que quien tiene el estado del proyecto se entere de que ya no está. */
  onUnpublished?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState('')
  const [confirming, setConfirming] = useState(false)
  /* Aparte de `publishedSubdomain`: quien nos lo pasa lo tiene cacheado, así que
     sin esto la fila seguiría diciendo «en línea» justo después de pulsar, que
     es cuando más se mira. */
  const [pulledDown, setPulledDown] = useState(false)
  const live = Boolean(publishedSubdomain) && !pulledDown

  const unpublish = async () => {
    setConfirming(false)
    setBusy(true)
    setFailed('')
    try {
      await unpublishSite(projectId, token)
      setPulledDown(true)
      onUnpublished?.()
    } catch (error: unknown) {
      console.error('[publish] no se pudo despublicar:', error)
      setFailed(t('pub.unpublishFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className={`dash-publish-state${live ? ' is-live' : ''}`} aria-label={t('publish.site.title')}>
        <span className={`dash-widget-glass-icon dash-widget-glass-icon--project${live ? ' is-published' : ''}`} aria-hidden="true">
          {live ? <IconRocket size={18} /> : <IconCloudOff size={18} />}
        </span>
        <div className="dash-publish-state-text">
          <strong>{t(live ? 'publish.site.live' : 'publish.site.off')}</strong>
          <small>{failed || t(live ? 'publish.site.liveBody' : 'publish.site.offBody')}</small>
        </div>
        {live ? (
          <button type="button" className="dash-secondary-btn" disabled={busy} onClick={() => setConfirming(true)}>
            {busy
              ? <><IconLoader2 size={14} className="dash-spin" /> {t('publish.site.unpublishing')}</>
              : <><IconWorldOff size={14} aria-hidden="true" /> {t('publish.site.unpublish')}</>}
          </button>
        ) : onOpenEditor ? (
          <button type="button" className="dash-primary-btn dash-new-project-btn" title={t('publish.site.openEditorHint')} onClick={onOpenEditor}>
            <IconLayout size={14} aria-hidden="true" /> {t('publish.site.openEditor')}
          </button>
        ) : null}
      </section>

      {confirming ? (
        <ConfirmDialog
          title={t('publish.site.confirmTitle')}
          message={t('publish.site.confirmBody')}
          confirmLabel={t('publish.site.unpublish')}
          cancelLabel={t('common.cancel')}
          onConfirm={() => { void unpublish() }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </>
  )
}
